import { z } from 'zod';
import { MAIN_INCOME_SOURCE } from '@Types/consts.ts';
import type { LiliPlanType } from '@Pages/happyFlowPages/planSelection/types.ts';
import type { RegionCode } from 'google-libphonenumber';

export interface ICustomerSlice {
  customer: CustomerData;
  isBusinessBuildEnabled: boolean;
  setIsBusinessBuildEnabled: (isBusinessBuildEnabled: boolean) => void;
  setCustomerData: (data: CustomerData) => void;
  setCustomerAccessToken: (token: string) => void;
  getCustomerData: () => CustomerData;
  isMultiBusiness: () => boolean;
  isAccountantAsOwner: () => boolean;
  isPhoneCountryCodeUs: () => boolean;
  getIsUsFlow: () => boolean;
  getIsSoleProp: () => boolean;
  getIsUboValidForNonUsFlow: () => boolean;
  setAccessToken: (token: string) => void;
}

export interface CustomerData {
  externalId?: string;
  customerId?: string;
  partnerId?: string;
  accessToken?: string;
  didCompleteNonUSEDD?: boolean;
  customerLogoUrl?: string;
  onboardingStatus?: OnboardingStatus;
  nameTooLong?: boolean;
  plan?: LiliPlanType;
  planDiscountContent?: string;
  business?: { status?: BusinessStatus | undefined; businessType?: string };
  campaignName?: string;

  isPersonalAndBusinessAddressSame?: boolean;
  customerData: {
    email?: string;
    password?: string;
    phoneNumber?: string;
    phoneCountryCode?: RegionCode;
    firstName?: string;
    lastName?: string;
    cardFirstName?: string;
    cardLastName?: string;
    birthDate?: string;
    ssn?: string;
    customerBusinessData: CustomerBusinessData;
    onboardingTypeEnum: OnboardingTypeEnum;
    customerAddressData: AddressData;
    businessAddressData: AddressData;
    registeredBusinessAddressData?: AddressData;
    mailingAddressData?: MailingAddressData;
    cardShipmentAddressType?: CardShipmentAddressType;
    customerEdd?: string;
    idNumber?: string;
    idType?: string;
    idCountry?: string;
    idGovNumber?: string;
    idExpirationDate?: string;
    partner?: boolean;
    pro?: boolean;
    lastStepData?: IPickUpWhreYouLeft;
  };
  allowEditingPersonalDetails?: boolean;
  hasSoleProprietor?: boolean;
  availablePromoCodes?: IAvailablePromoCodes;
  bank?: bankTypes;

  isStar?: boolean;
  isNonUsSupported?: boolean;
  idCountry?: string;
  nationalIdDescription?: string;

  eddData?: {
    description: string;
    customerCountry: string;
    vendorCountry: string;
    monthlyVolume: string;
    monthlyTransactions: string;
  };
}

export interface IPickUpWhreYouLeft {
  version: number;
  stepName: string;
}
interface CustomerBusinessData {
  externalId?: string;
  segment?: string;
  naicsIndustry?: string;
  mainIncomeSource?: MAIN_INCOME_SOURCE;
  businessName?: string;
  incorporationState?: string;
  ein?: string;
  ownershipPercent?: number;
  naicsCode?: string;
  naicsTitle?: string;
  businessUbos?: {
    uboResponseList?: UboResponse[];
  };
}

export interface UboResponse {
  externalId: string;
  email: string;
  phoneNumber: string;
  phoneNumberCountryCode?: RegionCode | string;
  firstName: string;
  lastName: string;
  ssn?: string;
  birthDate: string;
  customerAddressData: AddressData;
  role: UBORoles;
  ownershipPercent: number;
  allowEditing: boolean;
  customerSSNData?: {
    birthDate?: string;
    idNumber?: string;
    idType?: string;
    idCountry?: string;
    idGovNumber?: string;
    idExpirationDate?: string;
  };
  fieldValidationErrors?: FieldValidationError[];
}

export enum FieldValidationObjectName {
  CONTACT = 'contact',
  PHONE_NUMBER = 'phoneNumber',
  IDENTITY = 'identity',
  HOME_ADDRESS = 'homeAddress',
  BUSINESS_DETAILS = 'businessDetails',
  BUSINESS_ADDRESS = 'businessAddress',
}

export enum FieldValidationFieldName {
  EMAIL = 'email',
  PHONE_NUMBER = 'phoneNumber',
  SSN = 'ssn',
  BIRTH_DATE = 'birthDate',
  LINE1 = 'line1',
  CITY = 'city',
  STATE = 'state',
  POSTAL_CODE = 'postalCode',
  BUSINESS_NAME = 'businessName',
  BUSINESS_TYPE = 'businessType',
  EIN = 'ein',
  NAICS = 'naics',
}

export interface FieldValidationError {
  fieldName: FieldValidationFieldName;
  objectName: FieldValidationObjectName;
  reason: string;
}

export enum UBORoles {
  PARTNER = 'PARTNER',
  PARTNER_FULL_ACCESS = 'PARTNER_FULL_ACCESS',
  PRIMARY_OWNER = 'PRIMARY',
}

export interface AddressData {
  city: string;
  state: string;
  line1: string;
  line2: string;
  postalCode: string;
  country?: string;
  sameAsPrimary: boolean;
}

export interface MailingAddressData {
  city: string;
  state: string;
  line1: string;
  line2?: string;
  postalCode: string;
  country?: string;
}

export interface IAvailablePromoCodes {
  PREMIUM?: string;
  PRO_V2?: string;
  SMART?: string;
}

export type bankTypes = 'SUNRISE' | 'CHOICE' | 'CHOICE_TO_SUNRISE';

export type CardShipmentAddressType = 'REGISTERED' | 'OPERATIONAL' | 'MAILING';

export enum OnboardingTypeEnum {
  STANDARD = 'STANDARD',
  MULTI_BUSINESS = 'MULTI_BUSINESS',
  PASSWORD_LESS = 'PASSWORD_LESS',
  NO_CONTINUE_BLOCK = 'NO_CONTINUE_BLOCK',
  NO_CONTINUE_PRIMARY = 'NO_CONTINUE_PRIMARY',
  ACCOUNTANT_AS_OWNER = 'ACCOUNTANT_AS_OWNER',
}

export enum BusinessStatus {
  NEW = 'NEW',
  PENDING = 'PENDING',
  RESUBMIT = 'RESUBMIT',
  APPROVED = 'APPROVED',
  PENDING_MIDDESK = 'PENDING_MIDDESK',
  NONE = 'NONE',
  KYC_PENDING = 'KYC_PENDING',
  REJECTED = 'REJECTED',
  undefined = 'undefined',
}

export enum OnboardingStatus {
  DURING_REGISTRATION = 'DURING_REGISTRATION',
  KYC_PASSED = 'KYC_PASSED',
  COMPLETED = 'COMPLETED',
  KYC_FIRST_FAILED = 'KYC_FIRST_FAILED',
  KYC_FAILED = 'KYC_FAILED',
  KYC_FAILED_PENDING = 'KYC_FAILED_PENDING',
  KYC_FAILED_UPLOADED_DOCS = 'KYC_FAILED_UPLOADED_DOCS',
  KYB_PENDING = 'KYB_PENDING',
  KYB_FAILED_FINAL = 'KYB_FAILED_FINAL',
  KYC_FAILED_PARTNER = 'KYC_FAILED_PARTNER',
  KYC_FAILED_FINAL = 'KYC_FAILED_FINAL',
  MFA_CHALLENGE = 'MFA_CHALLENGE',
}

const AddressDataSchema = z
  .object({
    city: z.string().optional(),
    state: z.string().optional(),
    line1: z.string().optional(),
    line2: z.string().optional(),
    postalCode: z.string().optional(),
    country: z.string().optional(),
    sameAsPrimary: z.boolean().optional(),
  })
  .optional();

const CustomerSSNData = z
  .object({
    birthDate: z.string().optional(),
    idCountry: z.string().optional(),
    idNumber: z.string().optional(),
    idType: z.string().optional(),
    idGovNumber: z.string().optional(),
    idExpirationDate: z.string().optional(),
  })
  .optional();

const FieldValidationErrorSchema = z.object({
  fieldName: z.nativeEnum(FieldValidationFieldName),
  objectName: z.nativeEnum(FieldValidationObjectName),
  reason: z.string(),
});

const UboResponseSchema = z.object({
  externalId: z.string().optional(),
  email: z.string().optional(),
  phoneNumber: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  ssn: z.string().optional(),
  birthDate: z.string().optional(),
  customerAddressData: AddressDataSchema,
  customerSSNData: CustomerSSNData,
  role: z.nativeEnum(UBORoles).optional(),
  ownershipPercent: z.number().optional(),
  allowEditing: z.boolean().optional(),
  fieldValidationErrors: z.array(FieldValidationErrorSchema).optional(),
});

const CustomerBusinessDataSchema = z.object({
  externalId: z.string().optional(),
  segment: z.string().optional(),
  naicsIndustry: z.string().optional(),
  mainIncomeSource: z
    .preprocess(
      (val) => (val === 'MMLLC' ? MAIN_INCOME_SOURCE.LLC : val),
      z.nativeEnum(MAIN_INCOME_SOURCE)
    )
    .optional(),
  businessName: z.string().optional(),
  incorporationState: z.string().optional(),
  ein: z.string().optional(),
  ownershipPercent: z.number().optional(),
  naicsCode: z.string().optional(),
  naicsTitle: z.string().optional(),
  businessUbos: z
    .object({
      uboResponseList: z.array(UboResponseSchema).optional(),
    })
    .optional()
    .optional(),
});

export const CustomerDataSchema = z.object({
  externalId: z.string().optional(),
  customerId: z.string().optional(),
  partnerId: z.string().optional(),
  accessToken: z.string().optional(),
  customerLogoUrl: z.string().optional(),
  onboardingStatus: z.nativeEnum(OnboardingStatus).optional(),
  nameTooLong: z.boolean().optional(),
  plan: z.any().optional(), // Replace with LiliPlanType schema if available
  planDiscountContent: z.string().optional(),
  business: z
    .object({
      status: z.nativeEnum(BusinessStatus).optional(),
      businessType: z.string().optional(),
    })
    .optional(),
  campaignName: z.string().optional(),
  isPersonalAndBusinessAddressSame: z.boolean().optional(),
  customerData: z.object({
    email: z.string().optional(),
    password: z.string().optional(),
    phoneNumber: z.string().optional(),
    phoneCountryCode: z.string().optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    cardFirstName: z.string().optional(),
    cardLastName: z.string().optional(),
    birthDate: z.string().optional(),
    ssn: z.string().optional(),
    customerBusinessData: CustomerBusinessDataSchema,
    onboardingTypeEnum: z.nativeEnum(OnboardingTypeEnum).optional(),
    customerAddressData: AddressDataSchema,
    businessAddressData: AddressDataSchema,
    registeredBusinessAddressData: AddressDataSchema,
    mailingAddressData: AddressDataSchema,
    cardShipmentAddressType: z.enum(['REGISTERED', 'OPERATIONAL', 'MAILING']).optional(),
    customerEdd: z.string().optional(),
    idNumber: z.string().optional(),
    idType: z.string().optional(),
    idCountry: z.string().optional(),
    idGovNumber: z.string().optional(),
    idExpirationDate: z.string().optional(),
    partner: z.boolean().optional(),
    pro: z.boolean().optional(),
    lastStepData: z
      .object({
        version: z.number(),
        stepName: z.string(),
      })
      .optional(),
  }),
  allowEditingPersonalDetails: z.boolean().optional(),
  hasSoleProprietor: z.boolean().optional(),
  availablePromoCodes: z
    .object({
      PREMIUM: z.string().optional(),
      PRO_V2: z.string().optional(),
      SMART: z.string().optional(),
    })
    .optional(),
  bank: z.enum(['SUNRISE', 'CHOICE', 'CHOICE_TO_SUNRISE']).optional(),
  isStar: z.boolean().optional(),
  isNonUsSupported: z.boolean().optional(),
  idCountry: z.string().optional(),
  nationalIdDescription: z.string().optional(),
});
